import { useGetOrdersQuery } from '@/service/positions/terminal-position.hook';
import {
  closeSnackbar,
  CustomContentProps,
  enqueueSnackbar,
  OptionsObject,
  SnackbarContent,
} from 'notistack';
import { forwardRef, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdCheck, MdClose, MdWarningAmber } from 'react-icons/md';

/// Au-delà, on cesse d'attendre l'ordre sur le compte et on le dit.
const VISIBLE_WITHIN_MS = 2 * 60_000;
/// La carte se retire seule une fois chaque ordre reçu.
const CLOSE_AFTER_MS = 4000;

declare module 'notistack' {
  interface VariantOverrides {
    orderProgress: { orderIds: string[]; detail?: string };
  }
}

interface OrderProgressProps extends CustomContentProps {
  orderIds: string[];
  detail?: string;
}

type StepState = 'pending' | 'grey' | 'green' | 'red' | 'amber';

function StepIcon({ state }: { state: StepState }) {
  if (state === 'pending')
    return (
      <span className="h-3 w-3 animate-spin rounded-full border border-neutral-700 border-t-neutral-300" />
    );
  const icon: Record<Exclude<StepState, 'pending'>, ReactNode> = {
    grey: <MdCheck size={14} className="text-neutral-400" />,
    green: <MdCheck size={14} className="text-[#4ade80]" />,
    red: <MdClose size={14} className="text-[#f87171]" />,
    amber: <MdWarningAmber size={14} className="text-[#fbbf24]" />,
  };
  return icon[state];
}

/// Suivi d'un ordre en deux étapes : envoyé par nos serveurs (coche grise),
/// puis reçu par le compte (coche verte). La seconde ne s'allume que si le
/// compte montre l'ordre, en position ouverte ou en attente — l'accusé d'un
/// exécuteur ne suffit pas.
const OrderProgressSnackbar = forwardRef<HTMLDivElement, OrderProgressProps>(
  ({ id, message, orderIds, detail, style, className }, ref) => {
    const { t } = useTranslation();
    const [expired, setExpired] = useState(false);
    const { data } = useGetOrdersQuery(orderIds, { enabled: !expired });
    const orders = data ?? [];
    const total = orderIds.length;
    const sent = orders.filter((o) => o.sentAt !== null || o.onAccount).length;
    const received = orders.filter((o) => o.onAccount).length;
    const rejected = orders.filter((o) => o.status === 'rejected');
    const done = received === total;
    const settled = received + rejected.length === total;

    useEffect(() => {
      const timer = setTimeout(() => setExpired(true), VISIBLE_WITHIN_MS);
      return () => clearTimeout(timer);
    }, []);
    useEffect(() => {
      if (!done) return;
      const timer = setTimeout(() => closeSnackbar(id), CLOSE_AFTER_MS);
      return () => clearTimeout(timer);
    }, [done, id]);

    const count = (n: number) => (total > 1 ? ` (${n}/${total})` : '');
    // Refusé avant de partir (copieur arrêté…) : nos serveurs ne l'ont pas
    // envoyé.
    const sentState: StepState =
      sent === total ? 'grey' : settled ? 'red' : expired ? 'amber' : 'pending';
    const receivedState: StepState = done
      ? 'green'
      : rejected.length > 0 && settled
        ? 'red'
        : expired
          ? 'amber'
          : 'pending';
    const receivedText =
      receivedState === 'red'
        ? t('orderProgress.rejected', { reason: rejected[0]?.comment ?? '' })
        : receivedState === 'amber'
          ? t('orderProgress.notVisible')
          : t('orderProgress.received') + count(received);

    return (
      <SnackbarContent ref={ref} role="alert" style={style} className={className}>
        <div className="flex w-[340px] max-w-[calc(100vw-32px)] items-start gap-3 rounded-[9px] border border-white/[0.08] bg-[#141417] py-2.5 pl-3 pr-2 font-main shadow-[0_12px_32px_rgba(0,0,0,0.5)]">
          <div className="min-w-0 flex-1 py-0.5">
            <div className="text-[13px] font-semibold leading-5 text-neutral-100">
              {message}
            </div>
            {detail && (
              <div className="mt-0.5 text-[12px] leading-4 text-neutral-400">
                {detail}
              </div>
            )}
            <div className="mt-2 flex flex-col gap-1.5 text-[12px] leading-4">
              <div className="flex items-center gap-2 text-neutral-300">
                <span className="flex w-3.5 justify-center">
                  <StepIcon state={sentState} />
                </span>
                {t('orderProgress.sent') + count(sent)}
              </div>
              <div className="flex items-center gap-2 text-neutral-300">
                <span className="flex w-3.5 justify-center">
                  <StepIcon state={receivedState} />
                </span>
                {receivedText}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => closeSnackbar(id)}
            className="rounded p-1 text-neutral-500 transition-colors hover:bg-white/5 hover:text-neutral-200"
          >
            <MdClose size={14} />
          </button>
        </div>
      </SnackbarContent>
    );
  },
);
OrderProgressSnackbar.displayName = 'OrderProgressSnackbar';

export default OrderProgressSnackbar;

/// Carte de suivi des ordres déposés, depuis le graphique comme depuis le
/// terminal : elle reste jusqu'à ce que le compte les montre, les refuse, ou
/// que l'attente dépasse deux minutes.
export function notifyOrderProgress(
  orderIds: string[],
  title: string,
  detail?: string,
  options?: Omit<
    OptionsObject<'orderProgress'>,
    'variant' | 'orderIds' | 'detail'
  >,
): void {
  enqueueSnackbar(title, {
    persist: true,
    preventDuplicate: false,
    ...options,
    variant: 'orderProgress',
    orderIds,
    detail,
  });
}
